<template lang="pug">
  #member-notification-settings
    .notif-section
      .notif-section__header
        h2.notif-section__title Email
        .notif-section__global
          span.notif-section__global-label Unsubscribe from all
          span.toggle-off Off
          BSwitch(
            :value="emailUnsubscribed"
            type="is-info"
            :disabled="!memberLoaded"
            @input="handleEmailField('unsubscribeAll', $event ? 1 : 0)"
          )
          span.toggle-on On
      template(v-if="memberLoaded")
        .notif-type-row
          .notif-type-row__info
            p.notif-type-row__title Program Admin updates
            p.notif-type-row__desc Receive emails from your Be Well program administrator
          .notif-type-row__toggle
            span.toggle-off Off
            BSwitch(
              :value="adminEmailsOn"
              type="is-info"
              @input="handleEmailField('adminEmails', $event ? 'Yes' : 'No')"
            )
            span.toggle-on On
        .notif-type-row
          .notif-type-row__info
            p.notif-type-row__title Weekly stats
            p.notif-type-row__desc Receive a weekly email with a recap of your stats and competition progress
          .notif-type-row__toggle
            span.toggle-off Off
            BSwitch(
              :value="weeklyStatsOn"
              type="is-info"
              @input="handleEmailField('weeklyStatsEmails', $event ? 1 : 0)"
            )
            span.toggle-on On
        .notif-type-row
          .notif-type-row__info
            p.notif-type-row__title Challenge milestone
            p.notif-type-row__desc Receive an email when you pass a milestone in a featured map challenge
          .notif-type-row__toggle
            span.toggle-off Off
            BSwitch(
              :value="milestoneOn"
              type="is-info"
              @input="handleEmailField('milestoneEmails', $event ? 1 : 0)"
            )
            span.toggle-on On
        .notif-type-row
          .notif-type-row__info
            p.notif-type-row__title Friend request
            p.notif-type-row__desc Receive an email when you have a new friend request
          .notif-type-row__toggle
            span.toggle-off Off
            BSwitch(
              :value="friendRequestOn"
              type="is-info"
              @input="handleEmailField('friendRequestEmails', $event ? 1 : 0)"
            )
            span.toggle-on On
        .notif-type-row
          .notif-type-row__info
            p.notif-type-row__title Comment on Journal Entry
            p.notif-type-row__desc Receive an email when someone comments on your journal entry
          .notif-type-row__toggle
            span.toggle-off Off
            BSwitch(
              :value="journalCommentOn"
              type="is-info"
              @input="handleEmailField('notificationPreference', $event ? 'email' : 'website')"
            )
            span.toggle-on On
      BLoading(:active="!memberLoaded" :is-full-page="false")

    .notif-section.mt-5
      .notif-section__header
        h2.notif-section__title Push
      BLoading(:active="!pushLoaded" :is-full-page="false")
      template(v-if="pushLoaded")
        .notif-category(
          v-for="(categoryMeta, categoryKey) in pushCategories"
          :key="categoryKey"
        )
          .notif-category__name {{ categoryMeta.label }}
          .notif-type-row(
            v-for="(typeMeta, typeKey) in categoryMeta.types"
            :key="typeKey"
          )
            .notif-type-row__info
              p.notif-type-row__title {{ typeMeta.title }}
              p.notif-type-row__desc {{ typeMeta.description }}
            .notif-type-row__toggle
              span.toggle-off Off
              BSwitch(
                :value="getPushTypeEnabled(categoryKey, typeKey)"
                type="is-info"
                @input="handlePushType(categoryKey, typeKey, $event)"
              )
              span.toggle-on On
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import analyticsTrackingMixin from "@/mixins/analyticsTrackingMixin";

const PUSH_CATEGORIES = {
  challenges: {
    label: "Challenges",
    types: {
      starting_soon: {
        title: "Challenge starting soon",
        description: "Be reminded 24 hours before a challenge begins"
      },
      started: {
        title: "Challenge started",
        description: "Receive a notification 24 hours after a challenge begins"
      },
      midway_reminder: {
        title: "Midway challenge reminder",
        description:
          "Receive a motivational check-in at the halfway point of your challenge"
      },
      ending_soon: {
        title: "Challenge ending soon",
        description: "Be reminded 48 hours before a challenge ends"
      },
      ended: {
        title: "Challenge has ended",
        description:
          "Be notified when your challenge has ended and results are available"
      },
      discussion_reply: {
        title: "New discussion board post",
        description:
          "Be alerted when new posts are added to the challenge discussion"
      }
    }
  },
  social: {
    label: "Social",
    types: {
      team_invitation: {
        title: "Team invitation",
        description: "Be notified when you are invited to join a team"
      },
      friend_request: {
        title: "Friend request",
        description: "Be notified when someone sends you a friend request"
      }
    }
  }
};

export default {
  name: "MemberNotificationSettings",

  mixins: [analyticsTrackingMixin],

  data() {
    return {
      pushCategories: PUSH_CATEGORIES
    };
  },

  computed: {
    ...mapGetters("members", ["member", "memberLoaded"]),
    ...mapGetters("notificationPreferences", {
      pushLoaded: "loaded",
      preferences: "preferences"
    }),
    adminEmailsOn() {
      return this._.get(this.member, "memberData.adminEmails") === "Yes";
    },
    weeklyStatsOn() {
      return !!this._.get(this.member, "memberData.weeklyStatsEmails");
    },
    milestoneOn() {
      return !!this._.get(this.member, "memberData.milestoneEmails");
    },
    friendRequestOn() {
      return !!this._.get(this.member, "memberData.friendRequestEmails");
    },
    journalCommentOn() {
      return (
        this._.get(this.member, "memberData.notificationPreference") === "email"
      );
    },
    emailUnsubscribed() {
      return !!this._.get(this.member, "memberData.unsubscribedFromEmails");
    }
  },

  mounted() {
    this.trackFeatureView("settings", "settings_notifications");
    this.fetchNotificationPreferences();
  },

  methods: {
    ...mapActions("members", ["updateMember", "fetchMember"]),
    ...mapActions("notificationPreferences", [
      "fetchNotificationPreferences",
      "updateTypePreference"
    ]),

    async handleEmailField(field, value) {
      try {
        await this.updateMember({
          memberId: this.$auth.user().id,
          formData: { [field]: value }
        });
      } catch {
        this.$buefy.toast.open({
          message: "Failed to update. Please try again.",
          type: "is-danger"
        });
        await this.fetchMember({
          memberId: this.$auth.user().id,
          setMember: true
        });
      }
    },

    getPushTypeEnabled(categoryKey, typeKey) {
      const prefs = this.preferences[categoryKey];
      if (!prefs || !prefs[typeKey]) return true;
      return prefs[typeKey].fcm !== false;
    },

    async handlePushType(categoryKey, typeKey, enabled) {
      try {
        await this.updateTypePreference({
          category: categoryKey,
          type: typeKey,
          channel: "fcm",
          enabled
        });
      } catch {
        this.$buefy.toast.open({
          message: "Failed to update. Please try again.",
          type: "is-danger"
        });
        await this.fetchNotificationPreferences();
      }
    }
  }
};
</script>

<style lang="scss" scoped>
#member-notification-settings {
  padding: 0.5rem 0;

  .notif-section {
    &__header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      border-bottom: 2px solid #dbdbdb;
      padding-bottom: 0.5rem;
      margin-bottom: 0.25rem;
    }

    &__title {
      font-size: 1.1rem;
      font-weight: 700;
    }

    &__global {
      display: flex;
      align-items: center;
      gap: 0.35rem;
      font-size: 0.8rem;
      color: #555;
    }

    &__global-label {
      margin-right: 0.5rem;
    }
  }

  .notif-category {
    &__name {
      font-size: 0.9rem;
      font-weight: 600;
      color: #363636;
      margin-top: 0.75rem;
      margin-bottom: 0.25rem;
    }
  }

  .notif-type-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0.5rem 0;
    border-bottom: 1px solid #f0f0f0;

    &__info {
      flex: 1;
      padding-right: 1rem;
    }

    &__title {
      font-size: 0.875rem;
      font-weight: 600;
      margin-bottom: 0.1rem;
    }

    &__desc {
      font-size: 0.78rem;
      color: #888;
      line-height: 1.3;
    }

    &__toggle {
      display: flex;
      align-items: center;
      gap: 0.3rem;
      white-space: nowrap;
    }
  }

  .toggle-off,
  .toggle-on {
    font-size: 0.78rem;
    color: #666;
  }
}
</style>
